"use client"

/**
 * Delete-draft confirmation — byte-level mirror of
 * delete-confirmation-dialog.tsx (AlertDialog canon, destructive literal on
 * the action) plus the async-disabled/Loader2 states from
 * cancel-private-listing-dialog.tsx, since deleting a draft also removes its
 * storage files via the delete route.
 */

import { Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface DeleteDraftDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isDeleting: boolean
  onConfirmAction: () => void
}

export function DeleteDraftDialog({
  open,
  onOpenChange,
  isDeleting,
  onConfirmAction,
}: DeleteDraftDialogProps) {
  const t = useTranslations("myListings")
  const tCommon = useTranslations("common")

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isDeleting) return
        onOpenChange(next)
      }}
    >
      <AlertDialogContent className="w-[calc(100vw-2rem)] sm:w-full">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("draftDeleteTitle")}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogBody>
          <AlertDialogDescription>{t("draftDeleteDesc")}</AlertDialogDescription>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>{tCommon("cancel")}</AlertDialogCancel>
          <AlertDialogAction
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(e) => {
              e.preventDefault()
              onConfirmAction()
            }}
          >
            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
